import { call, put, select, take } from 'redux-saga/effects';
import { push } from '../../../lib/redux-router';

import request from '../request';
import selectors from '../../../selectors';
import actions from '../../../actions';
import api from '../../../api';
import ActionTypes from '../../../constants/ActionTypes';
import Paths from '../../../constants/Paths';
import orm from '../../../orm';

export function* goToRoot() {
  yield put(push(Paths.ROOT));
}

export function* goToProject(projectId) {
  yield put(push(Paths.PROJECTS.replace(':id', projectId)));
}

export function* goToBoard(boardId) {
  yield put(push(Paths.BOARDS.replace(':id', boardId)));
}

export function* goToCard(cardId) {
  yield put(push(Paths.CARDS.replace(':id', cardId)));
}

export function* handleLocationChange() {
  const pathsMatch = yield select(selectors.selectPathsMatch);

  if (!pathsMatch) {
    return;
  }

  switch (pathsMatch.pattern.path) {
    case Paths.LOGIN:
    case Paths.OIDC_CALLBACK:
      yield call(goToRoot);

      return;
    default:
  }

  const isInitializing = yield select(selectors.selectIsInitializing);

  if (isInitializing) {
    yield take(ActionTypes.CORE_INITIALIZE);
  }

  let board;
  let users;
  let projects;
  let boardMemberships;
  let labels;
  let lists;
  let cards;
  let cardMemberships;
  let cardLabels;
  let tasks;
  let attachments;
  let deletedNotifications;
  let cardPointData;
  let cardPointAdvancedData;
  let cardPointArchivedData;
  let listReportData;
  let contentCardDefaults;
  let pointSystems;

  switch (pathsMatch.pattern.path) {
    case Paths.BOARDS:
    case Paths.CARDS: {
      const currentBoard = yield select(selectors.selectCurrentBoard);

      if (currentBoard && currentBoard.isFetching === null) {
        yield put(actions.handleLocationChange.fetchBoard(currentBoard.id));

        try {
          const response = yield call(request, api.getBoard, currentBoard.id, true);
          console.log('[handleLocationChange] Ответ от API getBoard:', response);
          ({
            item: board,
            included: {
              users,
              projects,
              boardMemberships,
              labels,
              lists,
              cards,
              cardMemberships,
              cardLabels,
              tasks,
              attachments,
              cardPointData,
              cardPointAdvancedData,
              cardPointArchivedData,
              listReportData,
              contentCardDefaults,
              pointSystems,
            },
          } = response);
          pointSystems = pointSystems || [];
          console.log('[handleLocationChange] Извлеченные данные - board:', board, 'pointSystems:', pointSystems);
        } catch (error) {
          console.error('[handleLocationChange] Ошибка при загрузке board:', error);
        }
      } else {
        console.log('[handleLocationChange] currentBoard не найден или уже загружается, currentBoard:', currentBoard);
      }

      if (pathsMatch.pattern.path === Paths.CARDS) {
        const notificationIds = yield select(selectors.selectNotificationIdsForCurrentCard);

        if (notificationIds && notificationIds.length > 0) {
          try {
            ({ items: deletedNotifications } = yield call(
              request,
              api.updateNotifications,
              notificationIds,
              {
                isRead: true,
              },
            ));
          } catch (error) {} // eslint-disable-line no-empty
        }
      }

      break;
    }
    default:
  }

  yield put(
    actions.handleLocationChange(
      board,
      users,
      projects,
      boardMemberships,
      labels,
      lists,
      cards,
      cardMemberships,
      cardLabels,
      tasks,
      attachments,
      deletedNotifications,
      cardPointData || [],
    ),
  );

  // Обрабатываем cardPointData для детекции закрытости задач
  if (cardPointData && cardPointData.length > 0) {
    const pointSystem = yield select(selectors.selectPointSystemForCurrentBoard);
    if (pointSystem) {
      for (const item of cardPointData) {
        if (item) {
          yield put(actions.fetchCardPointData.success(item.cardId, pointSystem.id, item));
        }
      }
    }
  }

  // Обрабатываем cardPointAdvancedData для подсчета суммы баллов
  if (cardPointAdvancedData && cardPointAdvancedData.length > 0) {
    const pointSystem = yield select(selectors.selectPointSystemForCurrentBoard);
    if (pointSystem) {
      // Группируем по cardId для обработки
      const cardPointAdvancedDataByCardId = {};
      cardPointAdvancedData.forEach((item) => {
        if (!cardPointAdvancedDataByCardId[item.cardId]) {
          cardPointAdvancedDataByCardId[item.cardId] = [];
        }
        cardPointAdvancedDataByCardId[item.cardId].push(item);
      });

      // Обрабатываем каждую карточку
      for (const cardId of Object.keys(cardPointAdvancedDataByCardId)) {
        const items = cardPointAdvancedDataByCardId[cardId];
        yield put(actions.fetchCardPointAdvancedData.success(cardId, pointSystem.id, items));
      }
    }
  }

  // Обрабатываем cardPointArchivedData для корректного отображения task_closed_at (taskClosedAt)
  // и для логики автоматического закрытия/сброса месяца
  if (cardPointArchivedData && cardPointArchivedData.length > 0) {
    for (const item of cardPointArchivedData) {
      if (item) {
        // Используем action для создания/обновления CardPointArchivedData
        // Это тот же action, что используется при закрытии задачи
        yield put({
          type: ActionTypes.CARD_POINT_DATA_COMPLETE__SUCCESS,
          payload: {
            archivedItem: item,
          },
        });
      }
    }
  }

  // Обрабатываем listReportData для отображения статуса закрытия месяца
  if (listReportData && listReportData.length > 0) {
    for (const item of listReportData) {
      if (item) {
        yield put(actions.fetchListReportData.success(item.listId, item));
      }
    }
  }

  // Обрабатываем contentCardDefaults для подсчета суммы баллов
  if (contentCardDefaults && contentCardDefaults.length > 0) {
    const pointSystem = yield select(selectors.selectPointSystemForCurrentBoard);
    if (pointSystem) {
      yield put(actions.fetchContentCardDefaults.success(pointSystem.id, contentCardDefaults));
    }
  }

  // Сохраняем pointSystems в Redux и загружаем DampingCoefficients
  // Если pointSystems не загружены из API, пытаемся получить их из Redux
  if (!pointSystems || pointSystems.length === 0) {
    const currentBoard = yield select(selectors.selectCurrentBoard);
    if (currentBoard) {
      const pointSystemFromRedux = yield select(selectors.selectPointSystemForCurrentBoard);
      if (pointSystemFromRedux) {
        console.log('[handleLocationChange] pointSystems не загружены из API, используем из Redux:', pointSystemFromRedux);
        pointSystems = [pointSystemFromRedux];
      }
    }
  }

  if (pointSystems && pointSystems.length > 0) {
    const currentBoard = yield select(selectors.selectCurrentBoard);
    const boardId = board?.id || currentBoard?.id;
    
    if (boardId) {
      console.log('[handleLocationChange] Сохраняем pointSystems:', pointSystems);
      // Сохраняем pointSystems в Redux (если еще не сохранены)
      for (const pointSystem of pointSystems) {
        console.log('[handleLocationChange] Сохраняем pointSystem в Redux:', pointSystem);
        yield put(actions.updatePointSystem.success(pointSystem));
      }

      // Находим pointSystem для текущей доски
      const pointSystem = pointSystems.find((ps) => String(ps.boardId) === String(boardId));
      if (pointSystem) {
        console.log('[handleLocationChange] Найден pointSystem, загружаем DampingCoefficients для pointSystemId:', pointSystem.id);
        const dampingCoefficientsService = yield import('./damping-coefficients');
        // Передаем pointSystemId напрямую
        yield call(dampingCoefficientsService.fetchDampingCoefficientsInCurrentBoard, pointSystem.id);
        
        // Проверяем, что DampingCoefficients действительно загружены в ORM
        const state = yield select();
        const session = orm.session(state.orm);
        const dampingCoefficientsInOrm = session.DampingCoefficient.all()
          .filter({ pointSistemId: pointSystem.id })
          .toRefArray();
        console.log('[handleLocationChange] DampingCoefficients в ORM после загрузки:', dampingCoefficientsInOrm);
      } else {
        console.log('[handleLocationChange] pointSystem не найден для boardId:', boardId, 'pointSystems:', pointSystems);
      }
    } else {
      console.log('[handleLocationChange] boardId не найден, board:', board, 'currentBoard:', currentBoard);
    }
  } else {
    console.log('[handleLocationChange] pointSystems отсутствуют, pointSystems:', pointSystems);
  }
}

export default {
  goToRoot,
  goToProject,
  goToBoard,
  goToCard,
  handleLocationChange,
};
